import type { Locale } from "./locale";
import type { Dictionary, MessagesOf } from "./translate";

/**
 * One area's strings (catalogue, staff, sign-in…) in both languages (R1-09f). The core dictionary
 * (`messages/id.ts`) holds what the shell and the register show and loads with the app; an area's
 * strings ship inside the routes that import it, so admin copy never weighs on the register's bundle.
 * Every key starts with `<name>.`, which keeps areas and the core apart.
 *
 * Define one as `export const staff = /* @__PURE__ *\/ defineArea(…)`: the annotation lets the bundler
 * drop the area from a route's always-loaded part once TanStack Router splits the component out
 * (otherwise the call counts as a side effect and the strings land in the entry chunk).
 */
export interface Area<D extends Dictionary = Dictionary> {
  readonly name: string;
  readonly id: D;
  readonly en: MessagesOf<D>;
}

export function defineArea<
  const N extends string,
  const D extends Readonly<Record<`${N}.${string}`, string>>,
>(name: N, id: D, en: NoInfer<MessagesOf<D>>): Area<D> {
  return { name, id, en };
}

const merged = new WeakMap<object, WeakMap<Area, Record<string, string>>>();

/** The core dictionary `core` (already in `locale`) with the area's strings in `locale` added; cached. */
export function areaMessages<C extends Record<string, string>, D extends Dictionary>(
  core: C,
  area: Area<D>,
  locale: Locale,
): C & MessagesOf<D> {
  let byArea = merged.get(core);
  if (!byArea) {
    byArea = new WeakMap();
    merged.set(core, byArea);
  }
  let messages = byArea.get(area);
  if (!messages) {
    messages = { ...core, ...(locale === "en" ? area.en : area.id) };
    byArea.set(area, messages);
  }
  return messages as C & MessagesOf<D>;
}
